import { useEffect, useState } from 'react';
import { api } from '@shared/api';
import type { CatalogModel, KeyLite, ProjectLite } from './sources';

export interface SearchData {
  models: CatalogModel[];
  projects: ProjectLite[];
  keys: KeyLite[];
}

const EMPTY: SearchData = { models: [], projects: [], keys: [] };
const TTL_MS = 60_000;
let cache: { at: number; data: SearchData } | null = null;

/** Lazy, session cached. */
export function useSearchData(enabled: boolean): SearchData {
  const [data, setData] = useState<SearchData>(cache?.data ?? EMPTY);
  useEffect(() => {
    if (!enabled || (cache && Date.now() - cache.at < TTL_MS)) return;
    let alive = true;
    void Promise.allSettled([
      api<{ data: CatalogModel[] }>('/v1/models'),
      api<{ data: ProjectLite[] }>('/v1/projects'),
      api<{ data: KeyLite[] }>('/v1/api-keys'),
    ]).then(([models, projects, keys]) => {
      const next: SearchData = {
        models: models.status === 'fulfilled' ? models.value.data : [],
        projects: projects.status === 'fulfilled' ? projects.value.data : [],
        keys: keys.status === 'fulfilled' ? keys.value.data : [],
      };
      cache = { at: Date.now(), data: next };
      if (alive) setData(next);
    });
    return () => {
      alive = false;
    };
  }, [enabled]);
  return data;
}
